import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onOpenContactForm }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-[0_1px_0_0_#E4E4E7]'
          : 'bg-white/70 backdrop-blur-xl'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-16 py-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center cursor-pointer group"
            data-testid="header-logo"
          >
            <span className="text-xl font-black tracking-tight text-[#09090B]" style={{fontFamily:'Outfit,sans-serif'}}>
              Trademark<span className="text-[#002FA7]">My</span>LogoName
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {[
              { label: 'Home', id: 'home' },
              { label: 'Services', id: 'services' },
              { label: 'Pricing', id: 'pricing' },
              { label: 'Testimonials', id: 'testimonials' },
              { label: 'FAQ', id: 'faq' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="link-underline text-sm font-medium text-zinc-500 hover:text-[#09090B] transition-colors px-3 py-2"
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+18005551234" className="flex items-center text-zinc-500 hover:text-[#09090B] transition-colors text-sm" data-testid="header-phone-link">
              <Phone className="w-3.5 h-3.5 mr-1.5" />
              (800) 555-1234
            </a>
            <Button
              onClick={onOpenContactForm}
              className="btn-lift bg-[#002FA7] hover:bg-[#001D66] text-white text-sm font-semibold px-5 rounded-md"
              data-testid="header-cta-button"
            >
              Get Started
            </Button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-zinc-700"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-zinc-200 pt-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              {['Home', 'Services', 'Pricing', 'Testimonials', 'FAQ'].map((label) => (
                <button
                  key={label}
                  onClick={() => scrollToSection(label.toLowerCase())}
                  className="text-zinc-600 hover:text-[#09090B] transition-colors text-left font-medium text-sm py-1.5"
                >
                  {label}
                </button>
              ))}
              <div className="pt-3 mt-1 border-t border-zinc-100">
                <a href="tel:+18005551234" className="flex items-center text-zinc-500 mb-3 text-sm">
                  <Phone className="w-4 h-4 mr-2" />(800) 555-1234
                </a>
                <Button
                  onClick={onOpenContactForm}
                  className="w-full btn-lift bg-[#002FA7] hover:bg-[#001D66] text-white font-semibold rounded-md"
                  data-testid="mobile-cta-button"
                >
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
